import type { ComponentPropsWithoutRef, ReactNode } from "react"

import { cn } from "../../lib/utils"

// Shared banner/notice shell. A tone drives the border+tint+accent (semantic, so
// it dark-switches); `dense` is the thin inline notice, the default is the
// card-style panel. Icon + title render in the tone accent; everything specific
// to a banner (lists, forms, tables, a trailing action) goes in as children.
type CalloutTone = "warning" | "destructive" | "success" | "info"

const TONE_CLASSES: Record<CalloutTone, { container: string; accent: string }> = {
  warning: { container: "border-warning/30 bg-warning/10", accent: "text-warning" },
  destructive: { container: "border-destructive/30 bg-destructive/10", accent: "text-destructive" },
  success: { container: "border-success/30 bg-success/10", accent: "text-success" },
  info: { container: "border-info/30 bg-info/10", accent: "text-info" },
}

type CalloutProps = Omit<ComponentPropsWithoutRef<"div">, "title"> & {
  tone?: CalloutTone
  icon?: ReactNode
  title?: ReactNode
  /** Trailing element on the header row, e.g. a Reopen button. */
  action?: ReactNode
  /** Thin inline notice instead of the default card-style panel. */
  dense?: boolean
  className?: string
}

export function Callout({
  tone = "warning",
  icon,
  title,
  action,
  dense = false,
  className,
  children,
  ...containerProps
}: CalloutProps) {
  const t = TONE_CLASSES[tone]
  const hasHeader = Boolean(icon ?? title ?? action)
  return (
    <div
      {...containerProps}
      className={cn(
        "border",
        dense ? "rounded-lg px-3 py-2 text-xs" : "rounded-xl p-4 shadow-card",
        t.container,
        className
      )}
    >
      {hasHeader && (
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            {icon ? <span className={cn("shrink-0", t.accent)}>{icon}</span> : null}

            {title ? <div className={cn("text-sm font-medium", t.accent)}>{title}</div> : null}
          </div>

          {action ?? null}
        </div>
      )}

      {children}
    </div>
  )
}
